import { useObjectUrl } from "@/hooks/useObjectUrl"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { FileText, X } from "lucide-react"
import { useState } from "react"
import {
  useNoteAttachmentBlob,
  type NoteAttachmentOut,
} from "../../../api/notesApi"
import { NoteAttachmentPreviewDialog } from "./NoteAttachmentPreviewDialog"
import { useInViewport } from "./useInViewport"

interface AttachmentProps {
  attachment: NoteAttachmentOut
  canRemove: boolean
  onOpen: (attachment: NoteAttachmentOut) => void
  onRemove?: (attachmentId: string) => void
}

function RemoveButton({
  attachment,
  onRemove,
}: {
  attachment: NoteAttachmentOut
  onRemove: (attachmentId: string) => void
}) {
  const { translate } = useTranslation()
  return (
    <button
      type="button"
      onClick={() => onRemove(attachment.id)}
      aria-label={translate("MC_NOTE_ATTACHMENT_REMOVE", {
        name: attachment.fileName,
      })}
      className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black"
    >
      <X size={11} />
    </button>
  )
}

function ImageAttachment({
  attachment,
  canRemove,
  onOpen,
  onRemove,
}: AttachmentProps) {
  const { translate } = useTranslation()
  // Only once it is near the screen: a note with many images must not download all
  // of them up front.
  const [ref, inViewport] = useInViewport<HTMLLIElement>()
  const { data: blob } = useNoteAttachmentBlob(attachment, inViewport)
  const src = useObjectUrl(blob)
  return (
    <li ref={ref} className="relative">
      <button
        type="button"
        onClick={() => onOpen(attachment)}
        aria-label={translate("MC_NOTE_ATTACHMENT_OPEN", {
          name: attachment.fileName,
        })}
        className="block h-20 w-20 overflow-hidden rounded-md border border-grey4 bg-grey3 hover:border-grey5"
      >
        {src && (
          <img
            src={src}
            alt={attachment.fileName}
            className="h-full w-full object-cover"
          />
        )}
      </button>
      {canRemove && onRemove && (
        <RemoveButton attachment={attachment} onRemove={onRemove} />
      )}
    </li>
  )
}

function PdfAttachment({
  attachment,
  canRemove,
  onOpen,
  onRemove,
}: AttachmentProps) {
  const { translate } = useTranslation()
  return (
    <li className="flex max-w-full items-center gap-1 rounded-md border border-grey4 bg-grey3 text-[12px] text-white">
      <button
        type="button"
        onClick={() => onOpen(attachment)}
        aria-label={translate("MC_NOTE_ATTACHMENT_OPEN", {
          name: attachment.fileName,
        })}
        className="flex min-w-0 items-center gap-1.5 px-2 py-1.5 hover:text-yellow1"
      >
        <FileText size={14} className="shrink-0 text-grey5" />
        <span className="truncate">{attachment.fileName}</span>
      </button>
      {canRemove && onRemove && (
        <button
          type="button"
          onClick={() => onRemove(attachment.id)}
          aria-label={translate("MC_NOTE_ATTACHMENT_REMOVE", {
            name: attachment.fileName,
          })}
          className="shrink-0 pr-2 text-grey5 hover:text-white"
        >
          <X size={13} />
        </button>
      )}
    </li>
  )
}

interface NoteAttachmentListProps {
  attachments: NoteAttachmentOut[]
  /** Writers only: shows the remove button on each attachment. */
  canRemove?: boolean
  onRemove?: (attachmentId: string) => void
}

/** A note's files: image thumbnails and PDF chips, each opening in a preview dialog. */
export function NoteAttachmentList({
  attachments,
  canRemove = false,
  onRemove,
}: NoteAttachmentListProps) {
  const [previewId, setPreviewId] = useState<string | null>(null)
  if (attachments.length === 0) return null
  // By id, so a file that is removed while it is on show closes its preview.
  const preview = attachments.find((a) => a.id === previewId) ?? null
  const open = (attachment: NoteAttachmentOut) => setPreviewId(attachment.id)
  return (
    <>
      <ul className="flex flex-wrap gap-2">
        {attachments.map((attachment) =>
          attachment.contentType.startsWith("image/") ? (
            <ImageAttachment
              key={attachment.id}
              attachment={attachment}
              canRemove={canRemove}
              onOpen={open}
              onRemove={onRemove}
            />
          ) : (
            <PdfAttachment
              key={attachment.id}
              attachment={attachment}
              canRemove={canRemove}
              onOpen={open}
              onRemove={onRemove}
            />
          ),
        )}
      </ul>
      <NoteAttachmentPreviewDialog
        attachment={preview}
        onClose={() => setPreviewId(null)}
      />
    </>
  )
}
